"use client";

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { getWishlistProductIds, toggleWishlistItem, removeWishlistItem } from "@/app/(public)/store/_actions";

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const [ids, setIds] = useState(() => new Set());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getWishlistProductIds().then((productIds) => {
      if (cancelled) return;
      setIds(new Set(productIds));
      setHydrated(true);
    });
    return () => { cancelled = true; };
  }, []);

  const isWishlisted = useCallback((productId) => ids.has(productId), [ids]);

  // Optimistic — flips immediately, reverts if the server action errors
  // (e.g. the customer isn't actually logged in).
  const toggleWishlist = useCallback(async (productId) => {
    const wasWishlisted = ids.has(productId);

    setIds((prev) => {
      const next = new Set(prev);
      if (wasWishlisted) next.delete(productId); else next.add(productId);
      return next;
    });

    const result = await toggleWishlistItem(productId);

    if (result?.error) {
      setIds((prev) => {
        const next = new Set(prev);
        if (wasWishlisted) next.add(productId); else next.delete(productId);
        return next;
      });
    }

    return result;
  }, [ids]);

  const removeFromWishlist = useCallback(async (productId) => {
    setIds((prev) => {
      const next = new Set(prev);
      next.delete(productId);
      return next;
    });
    await removeWishlistItem(productId);
  }, []);

  const wishlistCount = ids.size;

  const value = useMemo(
    () => ({ isWishlisted, toggleWishlist, removeFromWishlist, wishlistCount, hydrated }),
    [isWishlisted, toggleWishlist, removeFromWishlist, wishlistCount, hydrated]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
}

const WISHLIST_FALLBACK = {
  isWishlisted: () => false,
  toggleWishlist: async () => ({ error: "Wishlist unavailable." }),
  removeFromWishlist: async () => {},
  wishlistCount: 0,
  hydrated: false,
};

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  return ctx ?? WISHLIST_FALLBACK;
}
